import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CollectionFormData } from '../types';
import { DocumentPicker } from './DocumentPicker';
import { Separator } from '@/components/ui/separator';

const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Collection name must be at least 2 characters.',
  }),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive']),
  visibility: z.enum(['public', 'private', 'restricted']),
  documents: z.array(z.string()).optional(),
});

type FormValues = {
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  visibility: 'public' | 'private' | 'restricted';
  documents?: string[];
};
// type FormValues = z.infer<typeof formSchema>;

interface CollectionFormProps {
  initialData?: CollectionFormData;
  onSubmit: (data: CollectionFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CollectionForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}: CollectionFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      status: initialData?.status || 'active',
      visibility: initialData?.visibility || 'public',
      documents: initialData?.documents || [],
    },
  });

  function handleSubmit(values: FormValues) {
    onSubmit(values as CollectionFormData);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Collection Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter collection name" {...field} />
              </FormControl>
              <FormDescription>
                The name of the collection as it will appear in the system.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter collection description"
                  className="resize-none"
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                A brief description of the collection's contents and purpose.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Active collections are visible to users with appropriate permissions.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="visibility"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Visibility</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Controls who can view this collection.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="documents"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Documents</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  <DocumentPicker
                    selectedDocumentIds={field.value || []}
                    onDocumentsChange={field.onChange}
                    maxSelections={100}
                  />
                  {field.value && field.value.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {field.value.length} document{field.value.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              </FormControl>
              <FormDescription>
                Select documents to include in this collection. You can search and filter to find specific documents.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="animate-spin mr-2">⟳</span> Saving...
              </>
            ) : initialData ? (
              'Update Collection'
            ) : (
              'Create Collection'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
